import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useHistory } from 'react-router-dom';
import { APP_PATH_PACKAGE_PLAN } from '../../config';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    paddingBottom: theme.spacing(1)
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  titleIcon: {
    color: theme.palette.warning.main,
    fontSize: 28
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3)
  },
  mainText: {
    fontSize: '1rem',
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
    color: theme.palette.text.primary
  },
  highlightBox: {
    padding: theme.spacing(2),
    backgroundColor: `${theme.palette.warning.main}10`,
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.warning.main}`,
    marginBottom: theme.spacing(2),
    textAlign: 'center'
  },
  highlightText: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: theme.palette.warning.dark
  },
  benefitsList: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  benefitItem: {
    padding: theme.spacing(0.5, 0)
  },
  benefitIcon: {
    minWidth: 36,
    color: theme.palette.success.main
  },
  benefitText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
      fontWeight: 500
    }
  },
  infoText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    lineHeight: 1.5
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1)
  },
  buyButton: {
    padding: theme.spacing(1, 3),
    fontWeight: 600
  },
  cancelButton: {
    padding: theme.spacing(1, 3)
  }
}));

const NoCreditsBuyBundleDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();

  const handleViewPricing = () => {
    onClose();
    history.push(`${APP_PATH_PACKAGE_PLAN}?bundle=1`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="no-credits-dialog-title">
      <DialogTitle id="no-credits-dialog-title" className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <ErrorOutlineIcon className={classes.titleIcon} />
          <Typography variant="h6" component="span">
            No Job Credits Available
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.mainText}>
          You need job credits to publish this position. Purchase a bundle to
          get started and unlock your hiring workspace.
        </Typography>

        <Box className={classes.highlightBox}>
          <Typography className={classes.highlightText}>
            Credits never expire and include full hiring tools
          </Typography>
        </Box>

        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, marginTop: 16 }}>
          What's Included With Every Bundle:
        </Typography>

        <List className={classes.benefitsList}>
          <ListItem className={classes.benefitItem}>
            <ListItemIcon className={classes.benefitIcon}>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Drag-and-drop candidate pipeline"
              className={classes.benefitText}
            />
          </ListItem>
          <ListItem className={classes.benefitItem}>
            <ListItemIcon className={classes.benefitIcon}>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Interview scheduling with notifications"
              className={classes.benefitText}
            />
          </ListItem>
          <ListItem className={classes.benefitItem}>
            <ListItemIcon className={classes.benefitIcon}>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Direct candidate messaging"
              className={classes.benefitText}
            />
          </ListItem>
          <ListItem className={classes.benefitItem}>
            <ListItemIcon className={classes.benefitIcon}>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Company logo displayed with post"
              className={classes.benefitText}
            />
          </ListItem>
        </List>

        <Typography className={classes.infoText}>
          Choose from Starter (3 credits), Professional (10 credits), or Premium
          (25 credits) bundles. All bundles include the same powerful hiring
          tools—only visibility features differ.
        </Typography>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="default"
          variant="outlined"
          className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleViewPricing}
          color="primary"
          variant="contained"
          className={classes.buyButton}
          startIcon={<ShoppingCartIcon />}>
          View Pricing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NoCreditsBuyBundleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NoCreditsBuyBundleDialog;
